import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBookings } from '../hooks/useBookings';
import { useAvailability } from '../hooks/useAvailability';
import Topbar from '../components/layout/Topbar';
import { BookingList } from '../components/bookings/BookingItem';
import { StatCard, Panel, PanelHead, PanelBody, Button, Spinner } from '../components/ui';
import { useWindowSize } from '../hooks/useWindowSize';

export default function Dashboard() {
  const { beach, admin } = useAuth();
  const { orders, loading, stats, cancel, confirm } = useBookings(admin?.beachCode);
  const { getStatsForDate } = useAvailability(admin?.beachCode);
  const navigate = useNavigate();
  const { isMobile, isTablet } = useWindowSize();

  const today = new Date().toISOString().split('T')[0];
  const ombrelloniStats = getStatsForDate(today);

  const recentOrders = useMemo(() => orders.slice(0, 5), [orders]);

  // Formatta cifre € con 2 decimali e separatore virgola italiano.
  // Es. 8162.5999999999 → "8.162,60"
  const formatEuro = (n) => n.toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Revenue ultimi 7 giorni: solo ordini `confirmed`, con prorata sul range
  // di date (es. una prenotazione da 90€ per 3 giorni conta 30€/giorno).
  const weekRevenue = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      const revenue = orders
        .filter(o => o.status === 'confirmed' && o.selectedDate?.includes(dateStr))
        .reduce((sum, o) => {
          const nDays = Math.max(1, o.selectedDate?.length || 1);
          return sum + (o.totalPrice || 0) / nDays;
        }, 0);
      days.push({
        label: d.toLocaleDateString('it-IT', { weekday: 'short' }),
        dateStr,
        revenue,
        isToday: dateStr === today,
      });
    }
    return days;
  }, [orders, today]);

  const maxRevenue = Math.max(...weekRevenue.map((d) => d.revenue), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Topbar
        title="Panoramica"
        actions={
          <Button variant="primary" onClick={() => navigate('/bookings')}>
            + Nuova prenotazione
          </Button>
        }
      />

      <div style={{ padding: isMobile ? '16px' : '24px 28px', overflowY: 'auto', flex: 1 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '60px' }}>
            <Spinner size={32} />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '14px', marginBottom: '24px' }}>
              <StatCard
                label="Prenotazioni oggi"
                value={stats.todayOrders.length}
                accentColor="var(--ocean)"
              />
              <StatCard
                label="Incasso oggi"
                value={`€ ${formatEuro(stats.todayRevenue)}`}
                accentColor="var(--sand)"
              />
              <StatCard
                label="Ombrelloni liberi"
                value={ombrelloniStats.free}
                sub={`/ ${ombrelloniStats.total}`}
                accentColor="var(--teal)"
              />
              <StatCard
                label="In attesa conferma"
                value={stats.pendingCount}
                accentColor="#e74c3c"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '16px', marginBottom: '24px' }}>
              {/* Recent bookings */}
              <Panel>
                <PanelHead
                  title="Prenotazioni recenti"
                  subtitle="Ultime 5"
                  action={
                    <Button size="sm" onClick={() => navigate('/bookings')}>
                      Vedi tutte →
                    </Button>
                  }
                />
                <PanelBody style={{ padding: '8px 20px' }}>
                  <BookingList
                    orders={recentOrders}
                    onCancel={cancel}
                    onConfirm={confirm}
                    emptyMessage="Nessuna prenotazione ancora"
                  />
                </PanelBody>
              </Panel>

              {/* Right column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Availability summary */}
                <Panel>
                  <PanelHead title="Disponibilità oggi" />
                  <PanelBody style={{ padding: '12px 20px' }}>
                    {[
                      { label: 'Ombrelloni', free: ombrelloniStats.free, occ: ombrelloniStats.occupied },
                      { label: 'Lettini', free: beach?.disponibilita?.lettini ?? '—', occ: '—' },
                      { label: 'Cabine', free: beach?.disponibilita?.cabine ?? '—', occ: '—' },
                    ].map((row) => (
                      <div key={row.label} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 0', borderBottom: '1px solid var(--border)',
                        fontSize: '13px',
                      }}>
                        <span style={{ color: 'var(--text-soft)' }}>{row.label}</span>
                        <div style={{ display: 'flex', gap: '14px' }}>
                          <span style={{ color: '#4ade80', fontWeight: 500 }}>{row.free} <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 400 }}>liberi</span></span>
                          <span style={{ color: '#f87171', fontWeight: 500 }}>{row.occ} <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 400 }}>occup.</span></span>
                        </div>
                      </div>
                    ))}
                  </PanelBody>
                </Panel>

                {/* Quick actions */}
                <Panel>
                  <PanelHead title="Azioni rapide" />
                  <PanelBody style={{ padding: '12px 20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {[
                        { icon: '📋', label: 'Nuova prenotazione', to: '/bookings' },
                        { icon: '☂️', label: 'Mappa spiaggia', to: '/beach' },
                        { icon: '📅', label: 'Disponibilità', to: '/availability' },
                        { icon: '⚙️', label: 'Impostazioni', to: '/settings' },
                      ].map((a) => (
                        <button
                          key={a.label}
                          onClick={() => navigate(a.to)}
                          style={{
                            padding: '12px', borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border)', background: 'var(--surface)',
                            color: 'var(--text-soft)', fontSize: '12px', cursor: 'pointer',
                            textAlign: 'left', fontFamily: 'var(--font-sans)',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hover)'; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.color = 'var(--text-soft)'; }}
                        >
                          <div style={{ fontSize: '16px', marginBottom: '5px' }}>{a.icon}</div>
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </PanelBody>
                </Panel>
              </div>
            </div>

            {/* Revenue chart */}
            <Panel>
              <PanelHead title="Incasso ultimi 7 giorni" />
              <PanelBody>
                {weekRevenue.map((d) => (
                  <div key={d.dateStr} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', width: '30px', flexShrink: 0, textTransform: 'capitalize' }}>
                      {d.label}
                    </span>
                    <div style={{ flex: 1, height: '6px', background: 'var(--surface)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: '3px',
                        width: `${(d.revenue / maxRevenue) * 100}%`,
                        background: d.isToday ? 'var(--sand)' : 'var(--ocean)',
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-soft)', width: '80px', textAlign: 'right', flexShrink: 0 }}>
                      {d.revenue ? `€ ${formatEuro(d.revenue)}` : '—'}
                    </span>
                  </div>
                ))}
              </PanelBody>
            </Panel>
          </>
        )}
      </div>
    </div>
  );
}
